<template>
  <div
    :id="productWidgetId"
    ref="productWidgetRef"
    class="fa-width-auto productwidget"
    :app-language="locale"
    :app-name="appName$"
    :app-version="version$"
  >
    <template v-if="!isReadyWithPartner">
      <loading-alt />
    </template>

    <template v-else-if="hasOutOfBoundRegistration">
      <immigrant
        :route-name="outOfBoundsRouteName"
        @back="hasOutOfBoundRegistration = false"
      />
    </template>

    <template v-else>
      <template v-if="$props.isExpress && !basketStore.basket.existingCustomer">
        <div class="productwidget__unauthorized">
          <div
            class="h2"
            v-html="$t('productWidget.widget.notLoggedIn.headline')"
          />
          <basic-message
            :title-label="$t('productWidget.widget.notLoggedIn.title')"
            :content="$t('productWidget.widget.notLoggedIn.text')"
            :actions="[{
              label: $t('productWidget.widget.notLoggedIn.loginNow'),
              callback: () => entryLoginRef.openLoginModal()
            }]"
          />

          <login-action
            ref="entryLoginRef"
            :caller-id="productWidgetId"
            :channel="CHANNEL.PRODUCTWIDGET"
            :modal-id="entryLoginModalId"
          />
        </div>
      </template>

      <template v-else-if="isEntryLoginActive || (!showPreview && basketStore.basket.persons.length === 0)">
        <productwidget-entry
          ref="productWidgetEntryRef"
          @open-form="data => openModal(data)"
          @out-of-bound-registration="handleOutOfBoundRegistration"
          @restart="restartApplication()"
        >
          <template #login>
            <login-action
              v-if="isLoginEnabled || isEntryLoginActive"
              ref="entryLoginRef"
              :caller-id="productWidgetId"
              :channel="CHANNEL.PRODUCTWIDGET"
              :modal-id="entryLoginModalId"
            />
          </template>
        </productwidget-entry>
      </template>

      <template v-else>
        <productwidget-main
          :is-express="$props.isExpress"
          :is-preview="showPreview"
          :persons="displayPersons.mutable"
          :products="products"
          :selected-product-id="$props.selected"
          :source="$props.source"
          @open-form="modalRef.open()"
          @restart="restartApplication()"
        >
          <template #login>
            <login-action
              v-if="isLoginEnabled || isMainLoginActive"
              ref="mainLoginRef"
              :caller-id="productWidgetId"
              :channel="CHANNEL.PRODUCTWIDGET"
              :modal-id="mainLoginModalId"
            />
          </template>
        </productwidget-main>
      </template>

      <basic-modal.container
        id="personsModal"
        ref="modalRef"
      >
        <basic-modal.step
          :id="`form_${products}`"
          :step-title="$t('productWidget.form.title')"
          size="midi"
          type="transactional"
        >
          <basic-modal.content>
            <productwidget-form
              ref="formRef"
              :initial-person="initialPerson"
              @close-modal="modalRef.close()"
              @out-of-bound-registration="handleOutOfBoundRegistration"
            />
          </basic-modal.content>

          <basic-modal.footer
            :primary-label="$t('form.apply')"
            @click-primary="formRef.formRef.submit()"
            @click-secondary="modalRef.close()"
          />
        </basic-modal.step>
      </basic-modal.container>
    </template>
  </div>
</template>

<script setup>
  import { computed, inject, ref } from 'vue'

  import { ReactivityUtil } from '@shared/utils/Reactivity'

  import basketStore from '@shared/store/basket'
  import partnerStore from '@shared/store/partner'
  import useApplication from '@shared/hooks/useApplication'
  import useAuth from '@shared/hooks/useAuth'
  import useI18n from '@shared/hooks/useI18n'
  import usePersonDetails from '@shared/hooks/usePersonDetails'
  import useUrl from '@shared/hooks/useUrl'

  import LoadingAlt from '@shared/components/Container/LoadingAlt'
  import ProductwidgetEntry from '@shared/modules/WebWidgets/components/ProductWidget/components/Entry'
  import ProductwidgetMain from '@shared/modules/WebWidgets/components/ProductWidget/components/Main'
  import ProductwidgetForm from '@shared/modules/WebWidgets/components/ProductWidget/components/Form'
  import { BasicMessage, BasicModal } from '@shared/components/Basic'
  import { LoginAction } from '@shared/components/Login'
  import Immigrant from '@shared/components/Person/Immigrant'

  import { CHANNEL } from '@shared/config/constants'
  import { SOURCE } from '@shared/config/events'

  // HOOKS
  const { locale } = useI18n()
  const { isReadyWithPartner } = useApplication()
  const { restartApplication } = useAuth()
  const { getPersonsByMutability } = usePersonDetails()
  const { getParamFromUrl } = useUrl()

  // INIT
  const appName$ = inject('appName$')
  const version$ = inject('version$')
  const props = defineProps({
    isExpress: {
      type: Boolean,
      default: false
    },

    hasPreview: {
      type: Boolean,
      default: false,
    },

    products: {
      type: String,
      default: null
    },

    selected: {
      type: String,
      default: null
    },

    source: {
      type: String,
      default: SOURCE.PRODUCT_WIDGET,
    },
  })

  // DATA
  const entryLoginRef = ref(null)
  const hasOutOfBoundRegistration = ref(false)
  const outOfBoundsRouteName = ref(null)
  const mainLoginRef = ref(null)
  const modalRef = ref(null)
  const formRef = ref(null)
  const initialPerson = ref()
  const productWidgetEntryRef = ref(null)

  // COMPUTED
  const displayPersons = computed(() => {
    return getPersonsByMutability()
  })

  const isLoginEnabled = computed(() => {
    return (
      ![CHANNEL.DIGITALADVISOR, CHANNEL.ALVA].includes(basketStore.basket.channel) &&
      !partnerStore.isPrivateInsurer.value &&
      !partnerStore.isBroker.value &&
      !partnerStore.isCollective.value &&
      !basketStore.basket.existingCustomer
    )
  })

  const isEntryLoginActive = computed(() => {
    return entryLoginRef.value?.actionModalRef?.isActive
  })
  const isMainLoginActive = computed(() => {
    return mainLoginRef.value?.actionModalRef?.isActive
  })

  const entryLoginModalId = computed(() => {
    return `product-widget-entry-login-modal-${productIds.value}`
  })

  const mainLoginModalId = computed(() => {
    return `product-widget-main-login-modal-${productIds.value}`
  })

  const products = computed(() => {
    return !props.isExpress ? props.products : getParamFromUrl('products')
  })

  const productIds = computed(() => {
    return products.value?.replace(/,/g, '-')
  })

  const productWidgetId = computed(() => {
    return `product-widget-${productIds.value}`
  })

  const showPreview = computed(() => {
    return props.hasPreview && basketStore.basket.persons.length === 0
  })

  // METHODS
  function handleOutOfBoundRegistration(routeName) {
    modalRef.value.close()
    hasOutOfBoundRegistration.value = true
    outOfBoundsRouteName.value = routeName
  }

  function openModal(formData) {
    initialPerson.value = ReactivityUtil.clone(formData)
    modalRef.value.open()
  }
</script>

<style scoped>
.productwidget {
  position: relative;
  border-radius: var(--border-radius-res-m);
  background: var(--surface);
  padding: var(--dotcom-responsive-offset-responsive);
  box-shadow: var(--elevation-level-3);
  min-width: 320px;
  margin-left: calc(-1 * var(--dotcom-responsive-offset-responsive));
  margin-right: calc(-1 * var(--dotcom-responsive-offset-responsive));
}

.productwidget__unauthorized {
  display: flex;
  flex-direction: column;
  gap: var(--dotcom-responsive-spacing-res-l);

  .h2 {
    text-align: center;
  }

  .prompt {
    display: none;
  }
}

@media (--v-medium) {
  .productwidget {
    padding: var(--dotcom-responsive-gutter-responsive);
    margin-left: initial;
    margin-right: initial;
  }
}
</style>
